import React, { useState, useEffect, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  Chip,
  Link,
  CircularProgress,
  Box,
  Typography,
  Tooltip,
  IconButton,
  Dialog,
  DialogContent,
  Grid,
  TextField,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import toast from "react-hot-toast";
import { axiosConfig } from "../../axios/axiosConfig";
import { truncate } from "lodash";
import { formatCronToReadable } from "../../utils/formatCronToReadable";
import { formatTime24Hour } from "../../utils/formatTime24Hour";
import EditIcon from "@mui/icons-material/Edit";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";

const NOTIFICATION_SOUND_LABELS = {
  silent: "no_sound",
  alert: "custom_channel_id",
  profit_booked: "custom_channel_id_2",
  signal_from: "custom_channel_id_3",
  beep: "custom_channel_id_4",
  tni_new_beep: "custom_channel_id_5",
  tni_new_beep_low: "custom_channel_id_6",
};

const VALUE_TO_KEY = Object.fromEntries(
  Object.entries(NOTIFICATION_SOUND_LABELS).map(([k, v]) => [v, k])
);

const NotificationTable = ({ rows = [], loading, refreshList, planList = [] }) => {
  const [localRows, setLocalRows] = useState([]);
  const [togglingId, setTogglingId] = useState(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState({
    id: null,
    title: "",
    message: "",
    deeplink: "",
    imageUrl: "",
    plans: [],
    scheduledTime: "",
    channelId: "silent",
  });
  const [saving, setSaving] = useState(false);

  const allPlans = [
    { id: "Trial", plan_name: "Trial", plan_code: "trial" },
    { id: "annonymous_user", plan_name: "Anonymous User", plan_code: "annonymous_user" },
    ...planList
      .map((plan) => {
        const planData = plan.data?.[0];
        if (!planData?.id) return null;
        return {
          id: planData.id,
          plan_name: plan.plan_name,
          plan_code: planData.plan_code || plan.plan_name,
        };
      })
      .filter(Boolean),
  ];

  useEffect(() => setLocalRows(rows), [rows]);

  const handleToggleEnabled = async (id) => {
    setLocalRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, enabled: !r.enabled } : r))
    );
    try {
      setTogglingId(id);
      await axiosConfig({
        method: "post",
        url: `/tnibroadmin/notifications/scheduled/${id}/toggle/`,
      });
      toast.success("Notification updated");
      refreshList && refreshList();
    } catch (err) {
      toast.error("Failed to update");
      setLocalRows((prev) =>
        prev.map((r) => (r.id === id ? { ...r, enabled: !r.enabled } : r))
      );
    } finally {
      setTogglingId(null);
    }
  };

  const handleEditClick = (row) => {
    const [
      plans = [],
      title = "",
      message = "",
      deeplink = "",
      imageUrl = "",
      channelValue = null,
    ] = row.args || [];

    const normalizedPlans = Array.isArray(plans)
      ? plans
          .map((planCode) => {
            const foundPlan = allPlans.find(
              (p) => String(p.plan_code).toLowerCase() === String(planCode).toLowerCase()
            );
            return foundPlan ? foundPlan.id : null;
          })
          .filter(Boolean)
      : [];

    const time = formatCronToReadable(row.schedule);
    let channelKey = "silent";
    if (channelValue) {
      channelKey = VALUE_TO_KEY[channelValue] || "silent";
    }

    setEditData({
      id: row.id,
      title,
      message,
      deeplink,
      imageUrl,
      plans: normalizedPlans,
      scheduledTime: formatTime24Hour(time),
      channelId: channelKey,
    });
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    const payload = {
      title: editData.title,
      message: editData.message,
      subscription_plan_id: editData.plans,
      deeplink: editData.deeplink,
      image_url: editData.imageUrl,
      scheduled_time: editData.scheduledTime,
      channel_id: NOTIFICATION_SOUND_LABELS[editData.channelId],
    };

    try {
      setSaving(true);
      await axiosConfig({
        method: "post",
        url: `/tnibroadmin/notifications/scheduled/${editData.id}/edit/`,
        data: payload,
      });
      toast.success("Notification updated successfully");
      setEditOpen(false);
      refreshList && refreshList();
    } catch (err) {
      toast.error("Failed to update notification");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await axiosConfig({
        method: "post",
        url: `/tnibroadmin/notifications/scheduled/${id}/delete/`,
      });
      toast.success(response?.data?.response || "Notification deleted successfully");
      refreshList && refreshList();
    } catch (error) {
      const errMsg =
        error.response?.data?.response || error.message || "Failed to delete notification";
      toast.error(errMsg);
    }
  };

  if (loading) {
    return (
      <Box textAlign="center" p={4}>
        <CircularProgress size={28} sx={{ color: "#4527a0" }} />
      </Box>
    );
  }

  if (!localRows.length) {
    return (
      <Box textAlign="center" p={4}>
        <Typography sx={{ color: "#b39ddb", fontSize: 14 }}>No Scheduled Notifications</Typography>
      </Box>
    );
  }

  return (
    <>
      <TableContainer sx={{ overflowX: "auto", overflowY: "auto", maxHeight: "65vh", border: "1px solid #e2e8f0", borderRadius: "8px" }}>
        <Table
        stickyHeader
          size="small"
          sx={{
            width: "100%",
            "& .MuiTableCell-root": {
              padding: "6px 10px",
              fontSize: "12px",
              borderBottom: "1px solid #f0f4f8",
              whiteSpace: "nowrap",
            },
            "& .MuiTableBody-root .MuiTableRow-root": {
              "&:hover": { background: "#f0f7ff" },
              "&:nth-of-type(even)": { background: "#f8fafc" },
              "&:nth-of-type(even):hover": { background: "#e8f4ff" },
            },
          }}
        >
          <TableHead>
            <TableRow
              sx={{
                "& th": {
                  fontWeight: 700,
                  fontSize: "11px",
                  color: "#ffffff",
                  background: "#016551",
                  textTransform: "uppercase",
                  letterSpacing: "0.4px",
                  whiteSpace: "nowrap",
                  padding: "9px 10px",
                  borderBottom: "none",
                },
              }}
            >
              <TableCell>Plans</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Message</TableCell>
              <TableCell>Deeplink</TableCell>
              <TableCell>Image</TableCell>
              <TableCell>Channel</TableCell>
              <TableCell>Schedule</TableCell>
              <TableCell align="center">Enabled</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {localRows.map((row) => {
              const [
                plans = [],
                title = "",
                message = "",
                deeplink = "",
                imageUrl = "",
                channelValue = "silent",
              ] = row.args || [];

              return (
                <TableRow key={row.id}>
                  <TableCell>
                    {plans.slice(0, 2).map((p) => (
                      <Chip
                        key={p}
                        label={p}
                        size="small"
                        sx={{
                          mr: 0.5,
                          fontSize: "10px",
                          height: 20,
                          background: "#ede7f6",
                          color: "#4527a0",
                          fontWeight: 600,
                        }}
                      />
                    ))}
                    {plans.length > 2 && (
                      <Tooltip title={plans.join(", ")} arrow>
                        <Chip
                          label={`+${plans.length - 2}`}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: "10px", height: 20 }}
                        />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={title}>
                      <span>{truncate(title, { length: 28 })}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={message}>
                      <span>{truncate(message, { length: 35 })}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {deeplink ? (
                      <Link href={deeplink} target="_blank" sx={{ fontSize: 12, color: "#4527a0" }}>
                        Open
                      </Link>
                    ) : (
                      <Typography sx={{ color: "#9ca3af", fontSize: 12 }}>—</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {imageUrl ? (
                      <Link href={imageUrl} target="_blank" sx={{ fontSize: 12, color: "#4527a0" }}>
                        View
                      </Link>
                    ) : (
                      <Typography sx={{ color: "#9ca3af", fontSize: 12 }}>—</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={VALUE_TO_KEY[channelValue] || "silent"}
                      size="small"
                      sx={{
                        fontSize: "10px",
                        height: 20,
                        background: "#f0fdf4",
                        color: "#166534",
                        fontWeight: 600,
                      }}
                    />
                  </TableCell>
                  <TableCell>{formatCronToReadable(row.schedule)}</TableCell>
                  <TableCell align="center">
                    <Box display="flex" justifyContent="center" alignItems="center" gap={0.5}>
                      <Switch
                        checked={row.enabled}
                        onChange={() => handleToggleEnabled(row.id)}
                        disabled={togglingId === row.id}
                        size="small"
                      />
                      {togglingId === row.id && <CircularProgress size={14} />}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => handleEditClick(row)}
                        sx={{ color: "#4527a0", "&:hover": { background: "#ede7f6" } }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(row.id)}
                        sx={{ color: "#dc2626", "&:hover": { background: "#fef2f2" } }}
                      >
                        <DeleteForeverIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit Dialog */}
      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "12px", overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" },
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            background: "#04343a",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <EditIcon sx={{ color: "#7c4dff", fontSize: 18 }} />
            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 16 }}>
              Edit Notification
            </Typography>
          </Box>
          <IconButton
            onClick={() => setEditOpen(false)}
            sx={{ color: "#b39ddb", "&:hover": { color: "#fff" } }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 3, background: "#f8fafc" }}>
          <Box sx={{ width: "100%", maxWidth: 800, mt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Title"
                  value={editData.title}
                  onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Message"
                  value={editData.message}
                  onChange={(e) => setEditData({ ...editData, message: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Deeplink"
                  value={editData.deeplink}
                  onChange={(e) => setEditData({ ...editData, deeplink: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Image URL"
                  value={editData.imageUrl}
                  onChange={(e) => setEditData({ ...editData, imageUrl: e.target.value })}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Plans</InputLabel>
                  <Select
                    multiple
                    value={Array.isArray(editData.plans) ? editData.plans : []}
                    onChange={(e) => setEditData({ ...editData, plans: e.target.value })}
                    input={<OutlinedInput label="Plans" />}
                    renderValue={(selected) =>
                      selected
                        .map((id) => {
                          const plan = allPlans.find((p) => p.id === id);
                          return plan?.plan_code || plan?.plan_name || id;
                        })
                        .join(", ")
                    }
                  >
                    {allPlans.map((plan) => (
                      <MenuItem key={plan.id} value={plan.id}>
                        <Checkbox
                          size="small"
                          checked={
                            Array.isArray(editData.plans) &&
                            (editData.plans.includes(plan.id) ||
                              editData.plans.some((p) => String(p) === String(plan.id)))
                          }
                        />
                        <ListItemText
                          primary={plan.plan_code || plan.plan_name}
                          secondary={`${plan.plan_name} (ID: ${plan.id})`}
                        />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Channel</InputLabel>
                  <Select
                    value={editData.channelId}
                    label="Channel"
                    onChange={(e) => setEditData({ ...editData, channelId: e.target.value })}
                  >
                    {Object.keys(NOTIFICATION_SOUND_LABELS).map((key) => (
                      <MenuItem key={key} value={key}>{key}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Scheduled Time (HH:mm)"
                  value={editData.scheduledTime}
                  onChange={(e) => setEditData({ ...editData, scheduledTime: e.target.value })}
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleSaveEdit}
                  disabled={saving}
                  sx={{
                    background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)",
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: 14,
                    py: 1.2,
                    borderRadius: "8px",
                    textTransform: "none",
                    "&:hover": { background: "linear-gradient(135deg, #17a349 0%, #148040 100%)" },
                  }}
                >
                  {saving ? <CircularProgress size={22} sx={{ color: "white" }} /> : "Save Changes"}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default NotificationTable;
